/**
 * 无实际碰撞数据时的平地 fallback：以场景包围盒底部为地面，
 * 仅提供 Y 轴地面射线查询和简单胶囊推出，使 walk/avatar 模式可用。
 */
import type { Collision, PushOut, RayHit } from './collision';

class FlatGroundCollision implements Collision {
    private _groundY: number;

    readonly voxelResolution = 0.1;

    /**
     * @param groundY - 地面 Y 坐标（PlayCanvas 世界空间）。
     */
    constructor(groundY = 0) {
        this._groundY = groundY;
    }

    queryRay(
        _ox: number, oy: number, _oz: number,
        _dx: number, dy: number, _dz: number,
        maxDist: number
    ): RayHit | null {
        // 仅处理向下射线（dy < 0）命中地面
        if (dy >= 0) return null;
        const t = (this._groundY - oy) / dy;
        if (t < 0 || t > maxDist) return null;
        return { x: _ox + _dx * t, y: this._groundY, z: _oz + _dz * t };
    }

    querySphere(
        _cx: number, cy: number, _cz: number,
        radius: number,
        out: PushOut
    ): boolean {
        const penetration = this._groundY + radius - cy;
        if (penetration > 0) {
            out.x = 0;
            out.y = penetration;
            out.z = 0;
            return true;
        }
        return false;
    }

    queryCapsule(
        _cx: number, cy: number, _cz: number,
        halfHeight: number, radius: number,
        out: PushOut
    ): boolean {
        // 胶囊底部球心 Y
        const bottomSphereY = cy - halfHeight;
        const penetration = this._groundY + radius - bottomSphereY;
        if (penetration > 0) {
            out.x = 0;
            out.y = penetration;
            out.z = 0;
            return true;
        }
        return false;
    }

    querySurfaceNormal(
        _x: number, _y: number, _z: number,
        _rdx: number, _rdy: number, _rdz: number
    ): { nx: number; ny: number; nz: number } {
        return { nx: 0, ny: 1, nz: 0 };
    }

    isFreeAt(_x: number, y: number, _z: number): boolean {
        return y > this._groundY;
    }
}

export { FlatGroundCollision };
